/**
 * Que tenant viaja en la cabecera, y cuando se toca.
 *
 * Vive aparte de `auth.tsx` —y sin React— por la misma razon que
 * `activeEntity.ts`: es la regla que decide el ALCANCE de cada peticion, y una
 * regla que no se puede probar sola termina probandose en produccion.
 *
 * Aqui se probo en produccion. El efecto original hacia:
 *
 *     const memberships = profile?.memberships ?? []
 *     if (memberships.length === 0) { setActiveTenantHeader(null); return }
 *
 * es decir, trataba "el perfil todavia no cargo" igual que "esta persona no
 * tiene alcance". Cuando Firebase reemite el estado de sesion —renovacion de
 * token, o el `null` inicial antes de restaurar la sesion persistida— el perfil
 * vuelve a `null` un instante, la cabecera se borraba, y las consultas que se
 * refrescaban en esa ventana salian sin tenant. El backend las rechaza con
 * 400 y la pantalla que ya habia cargado bien se vaciaba un segundo despues.
 *
 * La distincion que faltaba: **no saber no es lo mismo que saber que no hay**.
 */
import type { Membership, UserProfile } from "@/lib/types"

export interface DecisionTenant {
  /** Que publicar en la cabecera. `null` significa "sin alcance". */
  header: string | null
  /** Si NO, la cabecera se deja como esta: todavia no se sabe nada. */
  publicar: boolean
  /** Tenant que debe quedar como activo en el estado, si cambia. */
  activo: string | null
}

/**
 * @param profile   perfil cargado, o `null` mientras no se sabe.
 * @param actual    tenant activo segun el estado/localStorage.
 */
export function decidirTenant(
  profile: UserProfile | null | undefined,
  actual: string | null,
): DecisionTenant {
  // Sin perfil no se toca nada. Al cerrar sesion la cabecera la limpia
  // `signOut`, que es donde si se sabe que ya no hay alcance.
  if (!profile) return { header: null, publicar: false, activo: actual }

  /*
    SOLO CUENTAN LAS MEMBRESIAS CON CLIENTE. `membershipsFromApi` ya no fabrica
    ninguna sin el, pero la regla se defiende sola: esta funcion elige
    `memberships[0]` y una sola fila sin `tenantId` a la cabeza de la lista
    devolvia `activo: null` con `publicar: true` —«ya lo se, y es ninguno»—
    dejando la raiz del producto esperando un cliente que no iba a llegar.
  */
  const memberships: Membership[] = (profile.memberships ?? []).filter((m) => m.tenantId)
  if (memberships.length === 0) {
    return { header: null, publicar: true, activo: null }
  }

  // Un tenant guardado que ya no esta en la cartera no se respeta: seria
  // mostrar los numeros de un cliente al que ya no se tiene acceso, o pedirlos
  // y comerse un 403 en cada pantalla.
  const vigente = memberships.some((m) => m.tenantId === actual)
  const resuelto = vigente ? actual : memberships[0].tenantId
  return { header: resuelto, publicar: true, activo: resuelto }
}
